// Privacy policy + terms for the hosted UK-CN product.
//
// IMPORTANT — the repo's LEGAL_DISCLAIMER.md describes the ORIGINAL product:
// a local CLI where the maintainer hosts nothing and is explicitly "not a data
// controller". None of that is true of this deployment. job.nice2.io stores
// accounts and CVs on servers the operator controls and runs the models on the
// operator's own hardware, which makes the operator a data controller under UK
// GDPR. These pages describe THIS service; that file still applies to people
// self-hosting the open-source project.
//
// Every factual statement here was checked against the code (schema.sql, the
// repos, llm-client.mjs, storage.mjs, the account-delete handler). Two things
// are deliberately NOT claimed, because they are not true today:
//   - there is no self-serve data export endpoint, so access/portability is
//     described as a manual request;
//   - the operator's legal identity is not hardcoded — it comes from env and
//     renders an explicit gap notice when unset, rather than a made-up name.

import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../lib/api';
import { useZh } from '../lib/useProduct';

type LegalInfo = { entity: string; contact: string; address: string };

function useLegal(): LegalInfo {
  const { data } = useQuery({
    queryKey: ['session'],
    queryFn: () => apiGet<{ legal?: LegalInfo }>('/api/session'),
    staleTime: 5 * 60 * 1000,
  });
  return data?.legal || { entity: '', contact: '', address: '' };
}

const LAST_UPDATED = '2026-07-26';

function Page({ title, children }: { title: string; children: React.ReactNode }) {
  const zh = useZh();
  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      <header style={{ padding: '22px 48px', borderBottom: '1px solid var(--line)' }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'inherit', textDecoration: 'none' }}>
          <div style={{ width: 28, height: 28, borderRadius: 7, background: 'var(--ink)', color: '#fff', display: 'grid', placeItems: 'center', fontFamily: 'Fraunces, var(--cjk-serif), serif', fontWeight: 700, fontSize: 14 }}>N</div>
          <div style={{ fontWeight: 600, fontSize: 14 }}>Nice2Job</div>
        </Link>
      </header>
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '48px 24px 80px' }}>
        <h1 className="serif" style={{ fontSize: 34, fontWeight: 400, margin: '0 0 6px' }}>{title}</h1>
        <p style={{ color: 'var(--ink3)', fontSize: 13, margin: '0 0 32px' }}>
          {zh ? `最后更新：${LAST_UPDATED}` : `Last updated: ${LAST_UPDATED}`}
        </p>
        <div style={{ fontSize: 14.5, lineHeight: 1.75, color: 'var(--ink)' }}>{children}</div>
        <p style={{ marginTop: 48, fontSize: 13 }}>
          <Link to="/privacy" style={{ color: 'var(--pri)' }}>{zh ? '隐私政策' : 'Privacy'}</Link>
          {' · '}
          <Link to="/terms" style={{ color: 'var(--pri)' }}>{zh ? '服务条款' : 'Terms'}</Link>
          {' · '}
          <Link to="/" style={{ color: 'var(--pri)' }}>{zh ? '返回首页' : 'Home'}</Link>
        </p>
      </div>
    </div>
  );
}

function H({ children }: { children: React.ReactNode }) {
  return <h2 style={{ fontSize: 17, fontWeight: 650, margin: '32px 0 8px' }}>{children}</h2>;
}

/** Rendered wherever the operator's legal identity is required but unconfigured. */
function OperatorIdentity() {
  const zh = useZh();
  const legal = useLegal();
  if (legal.entity && legal.contact) {
    return (
      <p>
        {zh ? '本服务的运营方（数据控制者）为 ' : 'This service is operated by (data controller): '}
        <strong>{legal.entity}</strong>
        {legal.address ? `, ${legal.address}` : ''}
        {zh ? '。数据保护相关问题请联系 ' : '. For data protection enquiries, contact '}
        <a href={`mailto:${legal.contact}`} style={{ color: 'var(--pri)' }}>{legal.contact}</a>
        {zh ? '。' : '.'}
      </p>
    );
  }
  return (
    <p style={{ border: '1px solid #d97706', background: 'rgba(217,119,6,0.08)', padding: '12px 14px', borderRadius: 6 }}>
      <strong>{zh ? '⚠ 运营方信息尚未配置。' : '⚠ Operator details not yet configured.'}</strong>{' '}
      {zh
        ? '本页面在正式对外开放前必须填写运营方的法律主体名称与联系邮箱（服务器环境变量 LEGAL_ENTITY / LEGAL_CONTACT_EMAIL）。在此之前请勿将本服务用于真实用户。'
        : 'The operator\'s legal entity and contact email (LEGAL_ENTITY / LEGAL_CONTACT_EMAIL) must be set before this service is offered to real users.'}
    </p>
  );
}

export function Privacy() {
  const zh = useZh();
  if (!zh) {
    return (
      <Page title="Privacy Policy">
        <OperatorIdentity />
        <H>What we store</H>
        <p>Your account (email address, a hashed password, display name), your profile (name, contact details, location, target roles), the CV text and PDFs you upload or generate, the job applications and evaluations you create, and per-day usage counters.</p>
        <H>Where it is stored</H>
        <p>In a PostgreSQL database hosted by Supabase in AWS eu-west-1 (Ireland), on a server in Oracle Cloud uk-london-1 (London), and — for PDF files only — in Cloudflare R2 object storage.</p>
        <H>AI processing</H>
        <p>Your CV and job descriptions are sent to language models running on hardware the operator controls. They are <strong>not</strong> sent to OpenAI, Anthropic or any other third-party model provider, unless you explicitly configure your own API key in Settings — in which case your data goes to that provider under their terms.</p>
        <H>Analytics</H>
        <p>None. There are no analytics, tracking pixels or advertising scripts. One cookie is set, <code>n2j_session</code>, solely to keep you signed in.</p>
        <p>Fonts are served from this site, not from a font CDN, so loading a page contacts no third party at all.</p>
        <H>Your rights</H>
        <p>You can delete your account and all associated data at any time from Settings; this also removes your stored PDF files. For a copy of your data, or any other request under UK GDPR, contact the operator at the address above — there is currently no self-service export, so these are handled manually.</p>
        <H>Passwords</H>
        <p>Passwords are hashed with argon2id and are never stored or logged in readable form.</p>
      </Page>
    );
  }
  return (
    <Page title="隐私政策">
      <OperatorIdentity />

      <H>我们收集并保存什么</H>
      <p>
        <strong>账号信息：</strong>邮箱地址、密码（以 argon2id 哈希保存，我们无法看到你的原始密码）、显示名称、登录时间。<br />
        <strong>个人资料：</strong>你在「我的资料」中填写的姓名、联系方式、所在地、LinkedIn、目标岗位等。<br />
        <strong>简历内容：</strong>你上传或生成的简历全文与 PDF 文件。<br />
        <strong>求职记录：</strong>你保存的职位、评估报告、申请状态。<br />
        <strong>用量计数：</strong>每天的生成/扫描次数，用于额度控制。
      </p>

      <H>数据存放在哪里</H>
      <p>
        数据库托管于 <strong>Supabase（AWS 爱尔兰 eu-west-1）</strong>；应用服务器位于 <strong>Oracle Cloud 伦敦机房（uk-london-1）</strong>；生成的 PDF 文件存放在 <strong>Cloudflare R2</strong> 对象存储。
      </p>

      <H>AI 如何处理你的简历</H>
      <p>
        你的简历和职位描述会发送到<strong>运营方自建、自己掌控的服务器上运行的语言模型</strong>进行处理，
        <strong>不会</strong>发送给 OpenAI、Anthropic 或任何第三方大模型服务商。
      </p>
      <p style={{ color: 'var(--ink2)' }}>
        唯一的例外：如果你在「设置」里主动填写了自己的第三方 API Key，那么你的数据会按你的选择发送给该服务商，并适用他们的隐私条款。
      </p>

      <H>统计与追踪</H>
      <p>
        <strong>没有任何统计、埋点或广告代码。</strong>我们只使用一个 Cookie（<code>n2j_session</code>），用途仅为保持登录状态。
      </p>
      <p style={{ color: 'var(--ink2)' }}>
        页面字体由本站自己提供，不经过任何字体 CDN，因此打开页面不会向任何第三方发出请求。
      </p>

      <H>你的权利</H>
      <p>
        你可以随时在「设置」中<strong>自助删除账号</strong>，这会一并删除数据库记录、你的文件目录，以及存储在 R2 上的 PDF。
      </p>
      <p>
        如需<strong>获取你的数据副本</strong>或行使 UK GDPR 下的其他权利，请邮件联系上方的运营方地址。
        目前<strong>尚未提供自助导出功能</strong>，这类请求由人工处理。
      </p>

      <H>数据保留</H>
      <p>
        账号存续期间我们持续保存上述数据；账号删除后即从数据库和对象存储中移除。
        数据库托管方（Supabase）保留最长 1 天的自动备份，备份中的数据会随备份轮换自然过期。
      </p>
    </Page>
  );
}

export function Terms() {
  const zh = useZh();
  if (!zh) {
    return (
      <Page title="Terms of Service">
        <OperatorIdentity />
        <H>What this service does</H>
        <p>Nice2Job helps you find UK early-career roles and generate CVs. It produces application materials for you to review and submit yourself. It never submits an application on your behalf.</p>
        <H>AI-generated content</H>
        <p>Output is generated by language models and may contain errors. You are responsible for checking every document before sending it to an employer.</p>
        <H>Beta service</H>
        <p>The service is provided free and as-is during beta, with no guarantee of availability, and no guarantee of any job outcome.</p>
        <H>Acceptable use</H>
        <p>One account per person. Do not upload other people's personal data, and do not attempt to overload the shared inference capacity.</p>
      </Page>
    );
  }
  return (
    <Page title="服务条款">
      <OperatorIdentity />

      <H>这个服务做什么</H>
      <p>
        Nice2Job 帮助你查找英国实习与毕业生岗位，并根据你的中文自述生成英文简历、针对具体岗位做定制。
      </p>
      <p>
        <strong>我们只生成材料，绝不替你投递。</strong>所有申请都必须由你本人确认后自行提交。
      </p>

      <H>关于 AI 生成内容</H>
      <p>
        简历内容由语言模型生成，<strong>可能出现错误、遗漏或不准确的表述</strong>。
        在把任何材料发给雇主之前，请你务必<strong>逐条核对</strong>——尤其是公司名称、时间、数字和学历信息。
        因内容错误造成的后果由使用者自行承担。
      </p>

      <H>关于测试期</H>
      <p>
        目前处于免费测试阶段，服务按「现状」提供：
        我们<strong>不保证</strong>服务持续可用，<strong>不保证</strong>生成质量，也<strong>不对求职结果作任何承诺</strong>。
        推理算力有限，高峰期可能需要排队。
      </p>

      <H>使用规范</H>
      <p>
        每人一个账号；请勿上传他人的个人信息；请勿以脚本或其他方式恶意占用共享算力。
        违反者的账号可能被暂停。
      </p>

      <H>账号与数据</H>
      <p>
        你可以随时自助删除账号。数据处理方式详见 <Link to="/privacy" style={{ color: 'var(--pri)' }}>隐私政策</Link>。
      </p>
    </Page>
  );
}
